import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Avatar,
  Chip,
  Button,
} from "@mui/material";
import { COFFEE_CALL_DUMMY_CHANNEL } from "../../../../constants/config";
import { useAuth } from "../../../../hooks";
import { ActiveCards } from "../../../../constants/appConstants";

const StreamCard = ({
  title,
  description,
  thumbnail,
  relativeTime,
}: {
  title: string;
  description: string;
  thumbnail: string;
  relativeTime: string;
}) => (
  <Card sx={{ width: 300, boxShadow: 3, borderRadius: 2 }}>
    <CardMedia component="img" height="160" image={thumbnail} alt={title} />
    <CardContent>
      <Typography fontWeight={600} fontSize={16} gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
      <Chip
        label={`Live ${relativeTime}`}
        color="error"
        size="small"
        sx={{ mt: 1 }}
      />
    </CardContent>
  </Card>
);

const CoffeeCallCard = ({
  name,
  bio,
  profileImage,
  isActive,
  onClick,
}: {
  name: string;
  bio: string;
  profileImage: string;
  isActive: boolean;
  onClick: (id: string) => void;
}) => {
  return (
    <Card
      onClick={() => onClick("1")}
      sx={{
        width: 280,
        boxShadow: 2,
        borderRadius: 3,
        textAlign: "center",
        p: 2,
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: "scale(1.03)",
          boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
        },
      }}
    >
      <Box sx={{ position: "relative", display: "inline-block" }}>
        <Avatar
          src={profileImage}
          alt={name}
          sx={{
            width: 100,
            height: 100,
            mx: "auto",
            border: "3px solid #8B5CF6",
          }}
        />
        {isActive && (
          <Box
            sx={{
              position: "absolute",
              bottom: 5,
              right: 5,
              width: 14,
              height: 14,
              borderRadius: "50%",
              bgcolor: "green",
              border: "2px solid white",
            }}
          />
        )}
      </Box>
      <CardContent>
        <Typography fontWeight={600} fontSize={16}>
          {name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {bio}
        </Typography>
      </CardContent>
    </Card>
  );
};

export const UserHomepage: React.FC = () => {
  const [joinCoffeeCall, setJoinCoffeeCall] = useState(false);
  const { userInfo } = useAuth();

  // if (joinCoffeeCall) {
  //   return (
  //     <VideoCall
  //       userId={userInfo?.id + ""}
  //       videoId={COFFEE_CALL_DUMMY_CHANNEL}
  //       onCallEnd={() => setJoinCoffeeCall(false)}
  //       displayName={`${userInfo?.first_name} ${userInfo?.last_name}`}
  //       profileImage={userInfo?.profile_image ?? ""}
  //     />
  //   );
  // }

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
      {/* Active Public Cards Section */}
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{ mb: 2, fontFamily: '"Lexend", sans-serif' }}
      >
        🎬 Active Public Cards
      </Typography>
      <Grid container spacing={3}>
        {[
          {
            title: "Live Music with Alex",
            description: "Join Alex for a live acoustic session.",
            thumbnail:
              "https://static.vecteezy.com/system/resources/thumbnails/037/044/052/small/ai-generated-studio-shot-of-black-headphones-over-music-note-explosion-background-with-empty-space-for-text-photo.jpg",
            relativeTime: "2 minutes ago",
          },
          {
            title: "Cooking with Chef Jamie",
            description: "Learn to cook Italian dishes.",
            thumbnail:
              "https://thumbs.dreamstime.com/b/chef-cooking-delicious-food-kitchen-ai-generated-chef-cooking-delicious-food-kitchen-324515320.jpg",
            relativeTime: "15 minutes ago",
          },
          {
            title: "Yoga for Beginners",
            description: "Start your yoga journey with simple poses.",
            thumbnail:
              "https://img.freepik.com/premium-photo/woman-doing-yoga-beach-with-mountain-background_865967-25537.jpg",
            relativeTime: "30 minutes ago",
          },
          {
            title: "Gaming Marathon",
            description: "Watch an exciting gaming session.",
            thumbnail:
              "https://t4.ftcdn.net/jpg/05/64/31/67/360_F_564316725_zE8llusnCk3Sfr9rdfKya6fV7BQbjfyV.jpg",
            relativeTime: "1 hour ago",
          },
          {
            title: "Tech Talk Live",
            description: "Discussing the latest in technology.",
            thumbnail:
              "https://img.freepik.com/premium-photo/illustration-design-headphones-with-podcast-microphone_1151123-2816.jpg?semt=ais_hybrid",
            relativeTime: "2 hours ago",
          },
        ].map((stream, index) => (
          <Grid item key={index}>
            <StreamCard {...stream} />
          </Grid>
        ))}
      </Grid>

      {/* Coffee Calls */}
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{ mt: 6, mb: 2, fontFamily: '"Lexend", sans-serif' }}
      >
        ☕ Available Coffee Calls
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          onClick={() => setJoinCoffeeCall(true)}
          sx={{
            fontWeight: 600,
            textTransform: "none",
            background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
            borderRadius: "12px",
            px: 3,
            py: 1.2,
            fontSize: "1rem",
            color: "white",
          }}
        >
          Join Coffee Call
        </Button>
      </Box>

      <Grid container spacing={3}>
        {ActiveCards.map((coffeeCall, index) => (
          <Grid item key={index}>
            <CoffeeCallCard
              {...coffeeCall}
              onClick={(id: string) => {
                // Update this based on your routing system
                // Example: navigate(`/request-coffee-call/${id}`);
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
